// ─────────────────────────────────────────────
// FORMAT HELPERS
// ─────────────────────────────────────────────

export const fmt = (n) => {
  const num = parseFloat(n) || 0;
  return '₦' + num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const fmtNum = (n) =>
  (parseFloat(n) || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtDate = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const day = String(dt.getDate()).padStart(2, '0');
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const year = dt.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return d;
  }
};

// ─────────────────────────────────────────────
// STATUS → COLOR MAP
// ─────────────────────────────────────────────
export const statusColor = (s) => {
  const map = {
    active: 'green', planning: 'cyan', completed: 'green',
    on_hold: 'amber', cancelled: 'red',
    approved: 'green', pending: 'amber', rejected: 'red',
    confirmed: 'green', delivered: 'green',
    draft: 'gray', revised: 'cyan', omitted: 'red',
    open: 'red', in_progress: 'amber', resolved: 'green', closed: 'gray',
    certified: 'green', critical: 'red', high: 'red',
    medium: 'amber', low: 'green',
    addition: 'green', omission: 'red', substitution: 'amber',
    issue: 'red', return: 'green', transfer: 'cyan', adjustment: 'amber',
  };
  return map[(s || '').toLowerCase()] || 'gray';
};

// ─────────────────────────────────────────────
// CSV EXPORT
// ─────────────────────────────────────────────
export const exportCSV = (data = [], filename = 'export') => {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(row =>
    headers.map(h => {
      const v = row[h];
      if (v === null || v === undefined) return '';
      if (typeof v === 'object') return `"${JSON.stringify(v).replace(/"/g, '""')}"`;
      const s = String(v);
      return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')
  );
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─────────────────────────────────────────────
// ROLE CHECKER
// ─────────────────────────────────────────────
/**
 * Checks if a user has at least one of the required roles.
 * 
 * @param {Object} user - The user object containing a 'roles' array
 * @param {Array} allowedRoles - Array of allowed role names, e.g., ['admin', 'project_manager']
 * @returns {Boolean} - True if the user has an allowed role, false otherwise
 */
export const hasRole = (user, allowedRoles) => {
  // If no specific roles are required, allow access
  if (!allowedRoles || allowedRoles.length === 0) return true;

  // Ensure the user has roles assigned
  if (!user || !user.roles || !Array.isArray(user.roles)) return false;

  // Extract the role names the user has
  const userRoleNames = user.roles.map(r => r.role_name);

  // Super admin typically has access to everything
  if (userRoleNames.includes('super_admin')) return true;

  // Check if any of the user's roles match the allowed roles
  return allowedRoles.some(role => userRoleNames.includes(role));
};


// ─── Permission Matrix ──────────────────────────────────────────────────────
// Permissions granted per role in User Access Control → Permission Matrix, as
// "<module>:<action>" strings on each role. The server enforces the same strings
// (backend/app/core/permissions.py); this only decides what the screen offers.
export const PERM = {
  // Module "view": also opens a module's dashboard card, sidebar entry and read-only screens to roles
  // outside the module's own `roles` list. Additive — a view permission never hides anything.
  COST_CONTROL_VIEW: 'cost_control:view',
  BOQ_HUB_VIEW: 'boq_hub:view',
  QS_HUB_VIEW: 'qs_hub:view',
  EXPENSE_MASTER_VIEW: 'expense_master:view',
  PAYMENT_VOUCHER_VIEW: 'payment_voucher:view',
  HR_PEOPLE_VIEW: 'hr_people:view',
  // Restrictive "view": only roles holding these can open the module at all (the server refuses the
  // rest). Every role that existed when this was introduced was granted them, so nobody lost access.
  DASHBOARD_VIEW: 'dashboard:view',
  ENTERPRISE_REQUISITION_VIEW: 'enterprise_requisition:view',
  MATERIAL_REQUEST_VIEW: 'material_request:view',
  MATERIAL_PO_VIEW: 'material_po:view',
  // HR Request Center: raise requests, see everyone's, change its settings (approving is the approval engine's)
  HR_REQUESTS_CREATE: 'hr_requests:create',
  HR_REQUESTS_VIEW_ALL: 'hr_requests:view_all',
  HR_REQUESTS_CONFIGURE: 'hr_requests:configure',
  // HR Request Center > Advanced: raise, see all / confidential, assign & track approved requests, configure, report
  ADV_REQUESTS_CREATE: 'adv_requests:create',
  ADV_REQUESTS_VIEW_ALL: 'adv_requests:view_all',
  ADV_REQUESTS_VIEW_CONFIDENTIAL: 'adv_requests:view_confidential',
  ADV_REQUESTS_ASSIGN: 'adv_requests:assign',
  ADV_REQUESTS_CONFIGURE: 'adv_requests:configure',
  ADV_REQUESTS_REPORT: 'adv_requests:report',
  // HR Expenses: record, see all, manage others' (cancel / archive / delete drafts), set budgets, configure types, report
  HR_EXPENSES_CREATE: 'hr_expenses:create',
  HR_EXPENSES_VIEW_ALL: 'hr_expenses:view_all',
  HR_EXPENSES_MANAGE: 'hr_expenses:manage',
  HR_EXPENSES_BUDGET: 'hr_expenses:budget',
  HR_EXPENSES_CONFIGURE: 'hr_expenses:configure',
  HR_EXPENSES_REPORT: 'hr_expenses:report',
  // Payroll: see the set-up, change it, change employees' payroll details
  PAYROLL_VIEW: 'payroll:view',
  PAYROLL_SETUP: 'payroll:setup',
  PAYROLL_EMPLOYEES: 'payroll:employees',
  PAYROLL_INPUTS: 'payroll:inputs',
  PAYROLL_LOANS: 'payroll:loans',
  PAYROLL_RUNS: 'payroll:runs',
  PAYROLL_COMPENSATION: 'payroll:compensation',
  PAYROLL_PAYMENTS: 'payroll:payments',
};

// ─── System Administration cards ─────────────────────────────────────────────
// One permission group per card: admin_<card>:view|add|edit|delete (Request Management: view|manage).
// Mirrors backend/app/core/permissions.py → ADMIN_CARDS. User Access Control, Roles Control, Workflow
// Configs and Settings are super-admin only and have none.
export const ADMIN_CARDS = [
  'projects', 'departments', 'pr_types', 'consideration_statuses', 'pr_ledgers', 'payment_methods', 'cost_centres',
  'cost_object_types', 'category_asset_types', 'cost_objects', 'disciplines', 'categories', 'uom', 'materials',
  'account_ledgers', 'package_types', 'budget_control_levels', 'cost_groups', 'expense_categories',
  'expense_sub_categories', 'dmr_categories', 'suppliers',
];
export const adminPerm = (card, action) => `admin_${card}:${action}`;
export const ADMIN_VIEW_PERMS = [...ADMIN_CARDS, 'request_management', 'users'].map(c => adminPerm(c, 'view'));
/** What the user may do on one Administration card: { view, add, edit, delete } (Request Management: { view, manage }). */
export const adminAccess = (user, card) => {
  const actions = card === 'request_management' ? ['view', 'manage'] : ['view', 'add', 'edit', 'delete'];
  return Object.fromEntries(actions.map(a => [a, can(user, adminPerm(card, a))]));
};

// ─── Users Management ───────────────────────────────────────────────────────
// admin_users:<action>, one tick each. Login as User, permanent delete and granting the super_admin
// role are super-admin only; a super-admin account can only be changed by a super admin; nobody
// changes their own roles. The server enforces all of it (backend/app/services/user_admin_guard.py).
export const USER_ACTIONS = ['view', 'create', 'edit', 'assign_roles', 'lock', 'deactivate', 'reset_password', 'mfa'];
const roleNameOf = (r) => (typeof r === 'string' ? r : (r?.role_name || r?.name || ''));
export const isSuperAdmin = (user) => (user?.roles || []).some(r => roleNameOf(r) === 'super_admin');
export const userAdminAccess = (user) => ({
  ...Object.fromEntries(USER_ACTIONS.map(a => [a, can(user, `admin_users:${a}`)])),
  isSuper: isSuperAdmin(user),
});
/** May `actor` change `target` at all? (A super-admin account is off-limits to everyone else.) */
export const canManageUser = (actor, target) => isSuperAdmin(actor) || !isSuperAdmin(target);

/** A nav item / hub card / link is visible by its `roles`, or by any of its `perms`. */
// `requires` is the opposite of `perms`: a permission the entry cannot be seen without.
export const canSeeEntry = (user, entry) =>
  (!entry?.requires || can(user, entry.requires))
  && (hasRole(user, entry?.roles) || (Array.isArray(entry?.perms) && entry.perms.length > 0 && can(user, ...entry.perms)));

/** True when the user is a super admin or any of their roles holds one of `perms`. */
export const can = (user, ...perms) => {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  if (roles.some(r => r?.role_name === 'super_admin')) return true;
  // the user's own grants (User Access Control); their revocations are already out of the roles' lists
  if ((user?.granted_permissions || []).some(p => perms.includes(p))) return true;
  return roles.some(r => (r?.permissions || []).some(p => perms.includes(p)));
};

// The department head is the first approver, so a request they send back never
// got past the requester's own department — it is not yet a working document.
// Those belong in My Drafts alongside the requester's unsent work, not in My
// Requisitions. A bounce from any LATER step (Cost Control, Approval Queue) is
// a working document coming back for correction and stays in My Requisitions.
//
// Keyed on returned_TO_step_order being null, i.e. the return landed with the
// requester rather than with another approver.
const DEPARTMENT_STEP_ORDER = 1;

export const isDepartmentBounce = (p) => {
  const status = (p?.request_status || '').toUpperCase();
  if (status !== 'RETURNED') return false;
  if (p?.returned_to_step_order != null) return false;   // held by an approver
  const from = p?.returned_from_step_order ?? p?.status_view?.returned_from_step_order;
  return from === DEPARTMENT_STEP_ORDER;
};

/** Everything My Drafts shows: unsent drafts, plus department-head bounces. */
export const belongsInMyDrafts = (p) =>
  (p?.request_status || 'DRAFT').toUpperCase() === 'DRAFT' || isDepartmentBounce(p);

/**
 * Whether a return should offer a consideration status.
 *
 * The status is Cost Control's own tracking tool — its Pending Consideration
 * panel is the only place one is ever read. That panel now shows anything Cost
 * Control has parked, wherever the request ended up, so the rule follows who
 * is doing the parking rather than only where it lands:
 *
 *   Cost Control returning   -> always. They are parking it and waiting on the
 *                               answer, whether it went to the requester or
 *                               back down to a department head.
 *   Approval Queue returning -> only when it goes back to Cost Control, since
 *                               that is the only case Cost Control picks up.
 *   Anyone else              -> never. A department head's return never
 *                               reaches that panel.
 *
 * `currentStepName` is the step doing the returning; `targets` and `value`
 * describe the chosen destination.
 */
export const offersConsiderationStatus = (currentStepName, targets, value) => {
  const from = (currentStepName || '').toLowerCase();
  if (/cost control/.test(from)) return true;
  if (!/approval queue/.test(from)) return false;
  if (!value || value === 'requester') return false;
  const target = (targets || []).find((t) => t.value === value);
  return /cost control/i.test(target?.label || '');
};

/**
 * Turn any thrown value into something worth showing a user.
 *
 * api.js uses fetch, which fails in two quite different ways:
 *   - the request never left      -> TypeError "Failed to fetch" / "NetworkError"
 *   - the server answered badly   -> Error carrying the backend's `detail`
 *
 * The first is jargon and needs translating; the second is usually the most
 * useful thing we can say, so it is passed through rather than replaced by a
 * generic "something went wrong".
 */
// ─── Validation error formatting ────────────────────────────────────────────
// FastAPI reports a 422 as an ARRAY of per-field problems, not a string:
//
//   { detail: [ { loc: ["body","items",0,"vat_percentage"],
//                 msg: "Input should be less than or equal to 100" } ] }
//
// `new Error(thatArray)` stringifies to "[object Object]", which is exactly
// what users used to be shown for every validation failure — the one kind of
// error where the server has already said precisely what is wrong and which
// field is wrong. These helpers put that reason back in front of the user.

// Acronyms that look wrong when a field name is spelled out word by word.
const FIELD_WORDS = {
  vat: 'VAT', boq: 'BOQ', uom: 'UOM', pr: 'PR', po: 'PO', gl: 'GL',
  hr: 'HR', id: 'ID', ids: 'IDs', qty: 'quantity', cc: 'Cost Control',
};

const humanizeField = (name) =>
  String(name).split('_').map((w) => FIELD_WORDS[w] || w).join(' ');

// Containers whose index means something to the person reading it.
const PATH_LABELS = { items: 'Line', hr_employees: 'Employee row' };

/** Turn a Pydantic `loc` tuple into something that points at a real field.
 *  ["body","items",0,"vat_percentage"] -> "Line 1 → VAT percentage" */
const fieldPath = (loc = []) => {
  const parts = [];
  loc.forEach((seg, i) => {
    // "body" only says it came from the payload, which the reader can see.
    // "query"/"path" are worth keeping — they say where to look instead.
    if (i === 0 && seg === 'body') return;
    if (typeof seg === 'number') {
      // A list index. Count from 1, the way the form numbers its rows.
      const parent = parts.pop();
      parts.push(parent ? `${parent} ${seg + 1}` : `#${seg + 1}`);
      return;
    }
    parts.push(PATH_LABELS[seg] || humanizeField(seg));
  });
  return parts.join(' → ');
};

// The app's own 422 handler (backend/app/core/exceptions.py) does not pass
// FastAPI's list through. It answers
//
//   { detail: "Validation error",
//     errors: [ { field: "body -> items -> 0 -> description",
//                 message: "String should have at most 300 characters" } ] }
//
// so the per-field problems sit in `errors`, with the location as one joined
// string. Turned back into { loc, msg } they read exactly like FastAPI's own.
const fromServerErrors = (errors) =>
  (Array.isArray(errors) ? errors : []).map((e) => ({
    loc: String(e?.field || '').split(' -> ').filter(Boolean).map((seg) => (/^\d+$/.test(seg) ? Number(seg) : seg)),
    msg: e?.message,
  }));

/** One line per rejected field: where it is, and exactly why it was rejected.
 *  Takes FastAPI's `detail` list, or — when `detail` is only a summary
 *  string — the `errors` list the app's validation handler sends beside it. */
export const formatValidationDetail = (detail, errors) => {
  if (!Array.isArray(detail)) detail = fromServerErrors(errors);
  if (!detail.length) return '';
  const lines = detail
    .map((d) => {
      const msg = (d?.msg || d?.message || '').toString().trim();
      if (!msg) return '';
      const where = fieldPath(d?.loc);
      return where ? `${where}: ${msg}` : msg;
    })
    .filter(Boolean);
  if (!lines.length) return '';
  // Everything wrong at once, but a payload that fails in thirty places
  // should not produce a thirty-line toast.
  const shown = lines.slice(0, 5);
  const rest = lines.length - shown.length;
  return shown.join('\n') + (rest > 0 ? `\n…and ${rest} more` : '');
};

// The only messages this app writes for itself. Everything else a user sees
// comes from the server verbatim; these exist because in these cases the
// server never answered, so there is nothing to quote.
//
// The unreachable case splits on what the failed call was doing, because the
// advice differs: a save can simply be sent again, while a page that never
// loaded needs reloading — and there is no submit button on it to press.
const UNREACHABLE_SUBMIT =
  'Could not reach the server — the request failed, kindly click submit again.';
const UNREACHABLE_LOAD =
  'Could not reach the server — kindly reload the page.';
// Names the limit rather than saying "too long": a reader who waited two full
// minutes is entitled to know that is the whole budget and not a random give-up.
const TIMEOUT_MESSAGE =
  'The server did not respond within 2 minutes, so the request was stopped.';

// `mutating` is set by api.js from the HTTP method. Note the explicit
// `=== false`: when it is UNKNOWN we deliberately fall to the submit wording,
// because telling someone to reload after a failed save can throw away a
// requisition they have not managed to send yet. Confusing beats destructive.
const unreachableMessage = (e) =>
  (e?.mutating === false ? UNREACHABLE_LOAD : UNREACHABLE_SUBMIT);

/**
 * Whether this user's requisitions skip Department Head Review.
 *
 * Display only — the routing decision is made on the server from the same
 * role, and the client is never trusted for it. This exists so the form can
 * name the step a submission will ACTUALLY reach, instead of promising a
 * department head review that will not happen.
 */
export const bypassesDepartmentReview = (user) =>
  (user?.roles || []).some(
    (r) => (r?.role_name || r?.name) === 'bypass_department_review'
  );

export const errorText = (e, fallback = 'Something went wrong. Please try again.') => {
  const detail = e?.response?.data?.detail ?? e?.data?.detail;
  const fieldErrors = e?.response?.data?.errors ?? e?.data?.errors;

  // 422 — the server named the field and the rule it broke. Say both.
  const validation = formatValidationDetail(detail, fieldErrors);
  if (validation) return validation;

  // Some handlers answer with an object rather than a string or a list.
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const inner = detail.msg || detail.message || detail.detail || detail.error;
    if (inner) return String(inner).trim();
  }

  // ── No HTTP response at all ───────────────────────────────────────
  // Nothing came back from the server, so there is no `detail` to quote.
  // api.js records what it does know — which call, how long it waited, which
  // host, and whether the call could have changed anything — and that is what
  // gets said, rather than "something went wrong".
  //
  // The safe/unsafe split is the part that matters. A GET can be retried
  // freely. A POST or PATCH may have been received and acted on before the
  // connection died, so telling someone to "try again" could double-submit a
  // requisition. They are told to check first.
  if (e?.kind === 'timeout' || e?.kind === 'unreachable') {
    return e.kind === 'timeout' ? TIMEOUT_MESSAGE : unreachableMessage(e);
  }

  // The server answered and said something. Show it EXACTLY as sent, including
  // a raw 500 — a database message naming the real table, column or enum is
  // the fastest route to a fix, and paraphrasing it throws that away.
  //
  // This early return matters more than it looks. The browser-jargon
  // heuristics below match on words like "timed out" and "load failed", which
  // also appear in genuine server errors — "canceling statement due to
  // statement timeout" is a real Postgres message. Without this, a server
  // error would be silently replaced by a client-side guess about the network.
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }

  const raw = (detail || e?.message || '').toString().trim();

  // From here down there was no response body — only a thrown browser error.
  // Browsers word this differently — Chrome "Failed to fetch", Firefox
  // "NetworkError when attempting to fetch resource", Safari "Load failed".
  if (!raw || /failed to fetch|networkerror|load failed|network request failed/i.test(raw)) {
    return unreachableMessage(e);
  }
  if (/aborted|timeout|timed out/i.test(raw)) {
    return TIMEOUT_MESSAGE;
  }
  return raw || fallback;
};

// ─── Requisition field validation ───────────────────────────────────────────
// All three requisition forms validate the same way: assemble a list of
// checks, turn the failures into the error map the inputs read for their red
// border, and into a plain-English list for the toast. Shared so the forms
// cannot drift apart on what counts as required or how it is announced.
//
// `key` must match what the input reads (e.g. `project_id_2` for line 3's
// project) — a check with no matching input validates silently, which is the
// bug this replaces.
export const runChecks = (checks) => {
  const errors = {};
  const missing = [];
  checks.forEach(({ key, label, ok }) => {
    if (ok) return;
    errors[key] = true;
    if (label && !missing.includes(label)) missing.push(label);
  });
  return { errors, missing, valid: Object.keys(errors).length === 0 };
};

// Names the first few missing fields rather than all of them: past four the
// toast turns into a wall of text, and the red borders are already pointing
// at every one of them.
export const missingFieldsMessage = (missing) => {
  if (!missing || missing.length === 0) return '';
  const MAX = 4;
  const head = missing.length === 1
    ? 'One required field is missing'
    : `${missing.length} required fields are missing`;
  const shown = missing.slice(0, MAX).join(', ');
  const rest = missing.length > MAX ? `, and ${missing.length - MAX} more` : '';
  return `${head}: ${shown}${rest}.`;
};

// ─── Returned-for-correction ────────────────────────────────────────────────
// A request sent backwards to an EARLIER APPROVER sits with that approver, not
// with the requester. For them, correcting it and sending it on is an
// `approve` — that is what re-enters the step which asked for the change. All
// three routes that offer this (the inbox button, the edit form's
// Save & Resubmit, and the details page) end up in the same place server-side,
// so these helpers exist to stop the three from *describing* it differently.
//
// The discriminator is returned_TO_step_order, not returned_FROM: the latter
// is written on every return, including ones that go all the way back to the
// requester, so it cannot tell an approver-held correction from a
// requester-held one.
export const isAwaitingCorrection = (item) =>
  item?.status_view
    ? !!item.status_view.awaiting_correction
    : item?.returned_to_step_order != null;

// Where a corrected request goes when it is sent on.
export const correctionTarget = (item) =>
  item?.status_view?.returned_from_step_name
  || item?.returned_from_step_name
  || 'the previous step';

// One sentence, worded once, for every screen that offers the resubmit.
export const correctionNote = (stepName) =>
  `This request was sent back to you for correction. Submitting returns it to ${stepName}, to the same approver who raised it.`;

// ─── "Others" free-text pickers ─────────────────────────────────────────────
// Request Type and Expense Category both offer an "Others" choice that reveals
// a text box. There is deliberately no "Others" row in either lookup table —
// expense_categories requires a cost group and a discipline on every row, and
// a placeholder there would show up in the cost reporting that reads it. So
// "Others" is a value the picker carries, the foreign key is left null, and
// the typed text goes in its own column.
//
// The server resolves the display name, so a reader never sees the word
// "Others" — only what was typed.
export const OTHER_OPTION = '__other__';

// "Others" goes at the TOP of every picker that offers it. Tacked on the end
// it is a scroll away in a long list, and somebody who cannot find what they
// want needs to see the escape hatch immediately rather than hunting for it.
// `alwaysVisible` keeps it through the search box — see Select in UI.js.
export const withOtherOption = (options) => [
  { value: OTHER_OPTION, label: 'Others', alwaysVisible: true },
  ...options,
];

// Kept as a name for the same thing, so the call sites that spell out the
// intent still read correctly.
export const withOtherOptionFirst = withOtherOption;

// What to send: the id, or null when "Others" is chosen.
export const otherAwareId = (value) => (value === OTHER_OPTION ? null : (value || null));

// What to send alongside it: the typed text, or null when a real option is
// chosen — so switching away from "Others" clears the stale text server-side.
export const otherAwareText = (value, text) =>
  (value === OTHER_OPTION ? ((text || '').trim() || null) : null);

// What to preselect when loading a saved record: free text present means the
// record was saved as "Others".
export const otherAwareValue = (id, otherText) =>
  (otherText ? OTHER_OPTION : (id || ''));

// ─── Consideration status on a Cost Control return ──────────────────────────
// Cost Control must file every return under a heading — it is what groups the
// Pending Consideration panel — so unlike other steps there is no "None".

// "Returned By Approval Queue" describes the Approval Queue sending work DOWN
// to Cost Control. Offering it to Cost Control's own return would file the
// request under a heading describing something that did not happen.
// Matched on name because the headings are rows an administrator maintains,
// not a fixed enum; keep this in step if the row is ever renamed.
const CC_EXCLUDED_STATUSES = ['returned by approval queue'];

export const considerationOptionsFor = (statuses, fromStepName) => {
  const list = statuses || [];
  if (!/cost control/i.test(fromStepName || '')) return list;
  return list.filter((c) => !CC_EXCLUDED_STATUSES.includes((c.name || '').trim().toLowerCase()));
};

// Picking a destination pre-selects the heading that matches it. A default,
// not a lock — the approver can still choose any other heading afterwards.
const DESTINATION_STATUS = [
  [/requester/i, 'returned to requester'],
  [/department head|dept head/i, 'returned to department head'],
];

export const defaultConsiderationFor = (targetLabel, statuses) => {
  const label = (targetLabel || '').toLowerCase();
  const match = DESTINATION_STATUS.find(([pattern]) => pattern.test(label));
  if (!match) return '';
  const wanted = match[1];
  const found = (statuses || []).find((c) => (c.name || '').trim().toLowerCase() === wanted);
  return found ? found.id : '';
};
